import { createContext, useContext, useEffect, useState } from 'react'
import type { LanguageCode } from '@/config/languages'
import { detectLanguageFromBrowser } from '@/utils/detect-language'

export type Language = LanguageCode

interface LanguageProviderProps {
  children: React.ReactNode
  defaultLanguage?: Language
  storageKey?: string
}

interface LanguageState {
  language: Language
  setLanguage: (lang: Language) => void
}

const LanguageContext = createContext<LanguageState>({
  language: 'en',
  setLanguage: () => null,
})

export function LanguageProvider({
  children,
  storageKey = 'menu-ui-language',
}: LanguageProviderProps) {
  const [language, setLang] = useState<Language>(() => {
    // Check if user has a saved language preference
    const savedLanguage = localStorage.getItem(storageKey) as Language
    
    if (savedLanguage) {
      // User has visited before, use their saved preference
      return savedLanguage
    }
    
    // First-time visitor: auto-detect language from browser locale
    const detectedLanguage = detectLanguageFromBrowser()
    return detectedLanguage
  })

  useEffect(() => {
    localStorage.setItem(storageKey, language)
  }, [language, storageKey])

  const setLanguage = (lang: Language) => setLang(lang)

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return ctx
}
